import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export function useRealtimeSync() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    // Create realtime channel for database changes
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'coach_teams'
        },
        (payload) => {
          console.log('Coach teams change:', payload);
          queryClient.invalidateQueries({ queryKey: ['teams'] });
          queryClient.invalidateQueries({ queryKey: ['coaches'] });
          queryClient.invalidateQueries({ queryKey: ['coach-teams'] });
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Data Updated',
              description: 'Team assignments have been updated by another user.',
              duration: 3000,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'coaches'
        },
        (payload) => {
          console.log('Coaches change:', payload);
          queryClient.invalidateQueries({ queryKey: ['coaches'] });
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Data Updated',
              description: 'Coach information has been updated.',
              duration: 3000,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'teams'
        },
        (payload) => {
          console.log('Teams change:', payload);
          queryClient.invalidateQueries({ queryKey: ['teams'] });
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Data Updated',
              description: 'Team information has been updated.',
              duration: 3000,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'skills_strategies'
        },
        (payload) => {
          console.log('Skills strategies change:', payload);
          queryClient.invalidateQueries({ queryKey: ['strategies'] });
          queryClient.invalidateQueries({ queryKey: ['skills-strategies'] });
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Data Updated',
              description: 'Skills strategies have been updated.',
              duration: 3000,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'players'
        },
        (payload) => {
          console.log('Players change:', payload);
          queryClient.invalidateQueries({ queryKey: ['players'] });
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Data Updated',
              description: 'Player information has been updated.',
              duration: 3000,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'skill_sessions'
        },
        (payload) => {
          console.log('🎯 CRITICAL: Skill sessions realtime change:', {
            event: payload.eventType,
            table: payload.table,
            record: payload.new || payload.old,
            timestamp: new Date().toISOString()
          });
          
          // AGGRESSIVE: Remove stale cache completely and force refetch
          queryClient.removeQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0] as string;
              return key?.includes('coach-comprehensive-metrics') || 
                     key?.includes('team-skill-sessions');
            }
          });
          
          // Force immediate refetch of ALL chart data
          queryClient.refetchQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0] as string;
              return key?.includes('coach-comprehensive-metrics') || 
                     key?.includes('team-skill-sessions');
            },
            type: 'all'
          });
          
          console.log('🚀 FORCE REFETCHED all chart queries after skill session change');
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Skills Updated',
              description: 'Player skill sessions have been updated. Charts force refreshed.',
              duration: 3000,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_sessions'
        },
        (payload) => {
          console.log('📅 User sessions realtime change:', {
            event: payload.eventType,
            table: payload.table,
            record: payload.new || payload.old,
            timestamp: new Date().toISOString()
          });
          
          // Broad invalidation for user session changes
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0] as string;
              return key?.includes('coach-comprehensive-metrics') || 
                     key?.includes('team-skill-sessions');
            }
          });
          
          console.log('📈 Invalidated dashboard queries after user session change');
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Sessions Updated',
              description: 'Training sessions have been updated. Dashboard refreshed.',
              duration: 3000,
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          console.log('👤 Profiles realtime change:', {
            event: payload.eventType,
            table: payload.table,
            record: payload.new || payload.old,
            timestamp: new Date().toISOString()
          });
          
          // Invalidate player-related queries
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0] as string;
              return key?.includes('coach-comprehensive-metrics') || 
                     key?.includes('players');
            }
          });
          
          console.log('👥 Invalidated player queries after profile change');
          
          if (payload.eventType !== 'INSERT') {
            toast({
              title: 'Profiles Updated',
              description: 'Player profiles have been updated.',
              duration: 3000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, toast]);
}